import { useNavigate } from "react-router-dom";
import { Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Footer from "@/components/Footer";
import liveStayLogo from "@/assets/live-stay-logo.png";
import { cn } from "@/lib/utils";
import {
  guestProfileTheme,
  ownerProfileTheme,
  type AuthProfileTheme,
} from "@/lib/authProfileTheme";
import AuthProfileBanner from "@/components/auth/AuthProfileBanner";

type AuthMode = "login" | "signup";

interface AuthTypeChoiceProps {
  mode: AuthMode;
}

type ProfileOption = {
  theme: AuthProfileTheme;
  description: string;
  highlights: string[];
  path: string;
};

const getOptions = (mode: AuthMode): ProfileOption[] => {
  const isLogin = mode === "login";

  const guestAndOwner: ProfileOption[] = [
    {
      theme: guestProfileTheme,
      description: isLogin
        ? "Acesse sua conta para reservar estadias, acompanhar reservas e usar sua área pessoal."
        : "Crie sua conta para buscar imóveis, favoritar opções, solicitar reservas e conversar com proprietários.",
      highlights: isLogin
        ? [
            "Visualizar e gerenciar suas reservas",
            "Salvar imóveis favoritos",
            "Enviar mensagens aos anunciantes",
          ]
        : [
            "Cadastro gratuito com acesso imediato",
            "Ideal para quem quer reservar estadias",
            "Não é necessário possuir imóvel",
          ],
      path: isLogin ? "/login?profile=client" : "/login?profile=client#cadastro",
    },
    {
      theme: ownerProfileTheme,
      description: isLogin
        ? "Entre com a conta de quem anuncia imóveis, gerencia disponibilidades e recebe solicitações de reserva."
        : "Cadastre-se para anunciar seu imóvel, publicar disponibilidades e receber reservas na LiveStay.",
      highlights: isLogin
        ? [
            "Gerenciar imóveis e disponibilidades",
            "Responder solicitações de reserva",
            "Acompanhar documentos e assinatura",
          ]
        : [
            "Para quem possui imóvel para anunciar",
            "Cadastro passa por aprovação da equipe",
            "Após aprovação, você publica seus anúncios",
          ],
      path: isLogin ? "/login?profile=owner" : "/login?profile=owner#cadastro",
    },
  ];

  return guestAndOwner;
};

const pageMeta: Record<
  AuthMode,
  { title: string; subtitle: string; actionLabel: string }
> = {
  login: {
    title: "Como você deseja entrar?",
    subtitle: "Escolha o tipo de conta para acessar o login correto.",
    actionLabel: "Continuar para login",
  },
  signup: {
    title: "Como você deseja se cadastrar?",
    subtitle: "Escolha o cadastro que corresponde ao seu perfil na plataforma.",
    actionLabel: "Continuar para cadastro",
  },
};

const AuthTypeChoice = ({ mode }: AuthTypeChoiceProps) => {
  const navigate = useNavigate();
  const options = getOptions(mode);
  const meta = pageMeta[mode];

  const handleOptionClick = (path: string) => {
    navigate(path);
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, path: string) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOptionClick(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 py-10">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="mb-4 gap-2"
              aria-label="Voltar para a página inicial"
            >
              <Home className="h-4 w-4" />
              Voltar para Home
            </Button>
            <img src={liveStayLogo} alt="Live Stay" className="h-20 mx-auto mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{meta.title}</h1>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">{meta.subtitle}</p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 max-w-3xl mx-auto">
            {options.map((option) => {
              const { theme } = option;

              return (
                <Card
                  key={theme.key}
                  role="button"
                  tabIndex={0}
                  aria-label={`${meta.actionLabel} como ${theme.label}`}
                  onClick={() => handleOptionClick(option.path)}
                  onKeyDown={(event) => handleOptionKeyDown(event, option.path)}
                  className={cn(
                    "cursor-pointer overflow-hidden border-2 p-0 transition-all shadow-md hover:shadow-xl",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                    theme.cardBorder,
                    theme.cardHoverBorder,
                    theme.cardBg,
                  )}
                >
                  <AuthProfileBanner theme={theme} />
                  <CardHeader className="pb-2 pt-4">
                    <CardTitle className={cn("text-xl", theme.accentText)}>{theme.label}</CardTitle>
                    <CardDescription className="text-xs uppercase tracking-wide font-medium">
                      {theme.tagline}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{option.description}</p>
                    <ul className="space-y-2">
                      {option.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-start gap-2 text-sm text-foreground">
                          <span
                            className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", theme.bulletColor)}
                            aria-hidden="true"
                          />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className={cn("w-full shadow-md", theme.buttonClass)} type="button">
                      {meta.actionLabel}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? (
              <>
                Ainda não tem conta?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/cadastro")}
                  className="text-primary font-medium hover:underline"
                  aria-label="Ir para cadastro"
                >
                  Cadastre-se
                </button>
              </>
            ) : (
              <>
                Já possui conta?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/entrar")}
                  className="text-primary font-medium hover:underline"
                  aria-label="Ir para login"
                >
                  Entrar
                </button>
              </>
            )}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AuthTypeChoice;
